import escapedString from 'escape-string-regexp';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import type { OperatorsResponse } from 'src/apis/iip-service/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useSearch } from 'src/hooks/use-search';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

export const operatorOptionSchema = formOptionSchema.merge(
  z.object({ shortName: z.string().optional() })
);

type OperatorOption = z.input<typeof operatorOptionSchema>;

export const OperatorSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'operatorId',
}) => {
  const [searchValue, setSearchValue, searchQuery] = useSearch('', {
    minLength: 1,
  });

  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<OperatorsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!searchQuery || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [searchQuery, searchRequest.response]);

  const searchResult = useMemo(() => {
    if (!searchQuery || !searchRequest.response) return;

    const escapedSearchQuery = new RegExp(
      escapedString(searchQuery.toLowerCase())
    );

    return searchRequest.response.data._embedded.operators
      .filter(
        (operator) =>
          operator.name.toLowerCase().match(escapedSearchQuery) ||
          operator.shortName?.toLowerCase().match(escapedSearchQuery)
      )
      .map((operator) => ({
        value: operator.id,
        label: operator.name,
        shortName: operator.shortName,
      })) satisfies OperatorOption[];
  }, [searchQuery, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      searchValueForCurrentOptions={searchValue}
      pending={searchRequest.pending}
      options={searchResult}
      stacked
      placeholder={placeholder}
      renderOptionHint={(option) => option.shortName}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>Short Name:</strong> {option.shortName}
        </>
      )}
    />
  );
};
